import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { noRightsGuard } from '@core/guards/no-rights.guard';
import { tripsGuard } from '@core/guards/trips.guard';
import { userRolesAndPermissionsGuard } from '@core/guards/user-roles-and-permissions.guard';
import { MainLayoutComponent } from '@features/layout/main-layout/main-layout.component';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { canEnterVismaExpense } from '../core/guards/visma-expense.guard';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { NoRightsComponent } from './no-rights/no-rights.component';
import { VismaExpenseComponent } from './visma-expense/visma-expense.component';
import { OnCallPageComponent } from './on-call-page/on-call-page.component';
import { EmissionsPageRouteName } from './emissions-page/emissions.routes';
import { DamageReportsRouteName } from './damage-reports-page/damage-reports.routes';
import { Feature } from '@shared/constants/feature';
import { isFeatureEnabledGuard } from '@core/guards/feature-enabled.guard';
import { SettingsRouteName } from './settings-page/settings.routes';
import { FleetRouteName } from './fleet/fleet.routes';
import { authGuard } from '@core/guards/authenticated.guard';
import { applicationMetaDataGuard } from '@core/guards/application-meta-data.guard';
import { userDomainGuard } from '@core/guards/user-domain.guard';
import { RouteNetworkRouteName } from './route-network-page/route-network.routes';
import { OnCallRouteName } from './settings-page/settings-route-names';
import { StatisticsRouteName } from './statistics-page/statistics.module';
import { AssetRouteName } from './assets-page/assets-page.module';
import { ReportsRouteName } from './reports-page/reports-page.module';
import { UserRouteName } from './users-page/users-page.module';
import { VehicleRouteName } from './vehicles-page/vehicles-page.module';
import { TripRouteName } from './trips-page/trips-page.module';
import { POIRouteName } from './poi-page/poi-page.module';
import { VippsPaymentsRouteName } from './vipps-payments/vipps-payments-routes';

export const pagesRoutes: Routes = [
  {
    path: '',
    redirectTo: `/${TripRouteName.Main}`,
    pathMatch: 'full',
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: POIRouteName.Main,
        loadChildren: () =>
          import('./poi-page/poi-page.module').then(m => m.PoiPageModule),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.POIRead]),
          () => isFeatureEnabledGuard(Feature.POI),
        ],
      },
      {
        path: TripRouteName.Main,
        loadChildren: () =>
          import('./trips-page/trips-page.module').then(m => m.TripsPageModule),
        canActivate: [tripsGuard, () => isFeatureEnabledGuard(Feature.Trips)],
      },
      {
        path: VehicleRouteName.Main,
        loadChildren: () =>
          import('./vehicles-page/vehicles-page.module').then(
            m => m.VehiclesPageModule
          ),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.VehicleRead]),
          () => isFeatureEnabledGuard(Feature.Vehicles),
        ],
      },
      {
        path: UserRouteName.Main,
        loadChildren: () =>
          import('./users-page/users-page.module').then(m => m.UsersPageModule),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.UserRead]),
          () => isFeatureEnabledGuard(Feature.Users),
        ],
      },
      {
        path: ReportsRouteName.Main,
        loadChildren: () =>
          import('./reports-page/reports-page.module').then(
            m => m.ReportsPageModule
          ),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.ReportRead]),
          () => isFeatureEnabledGuard(Feature.Reports),
        ],
      },
      {
        path: EmissionsPageRouteName.Main,
        loadChildren: () =>
          import('./emissions-page/emissions.routes').then(
            r => r.EMISSIONS_ROUTES
          ),
        canActivate: [
          () =>
            userRolesAndPermissionsGuard([RolesAndPermissions.EmissionRead]),
          () => isFeatureEnabledGuard(Feature.Emissions),
        ],
      },
      {
        path: DamageReportsRouteName.Main,
        loadChildren: () =>
          import('./damage-reports-page/damage-reports.routes').then(
            r => r.DAMAGE_REPORTS_ROUTES
          ),
        canActivate: [
          () => isFeatureEnabledGuard(Feature.DamageReport),
          () =>
            userRolesAndPermissionsGuard(
              [RolesAndPermissions.DamageRead, RolesAndPermissions.DamageEdit],
              [],
              true
            ),
        ],
      },
      {
        path: AssetRouteName.Main,
        loadChildren: () =>
          import('./assets-page/assets-page.module').then(
            m => m.AssetsPageModule
          ),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.AssetRead]),
          () => isFeatureEnabledGuard(Feature.Assets),
        ],
      },
      { path: 'access-denied', component: AccessDeniedComponent },
      {
        path: 'no-rights',
        component: NoRightsComponent,
        canActivate: [noRightsGuard],
      },
      {
        path: StatisticsRouteName.Main,
        loadChildren: () =>
          import('./statistics-page/statistics.module').then(
            m => m.StatisticsModule
          ),
        canActivate: [
          () =>
            userRolesAndPermissionsGuard([RolesAndPermissions.StatisticsRead]),
          () => isFeatureEnabledGuard(Feature.Statistics),
        ],
      },

      {
        path: SettingsRouteName.Main,
        canActivate: [
          () =>
            userRolesAndPermissionsGuard([RolesAndPermissions.SettingsRead]),
        ],
        loadChildren: () =>
          import('./settings-page/settings.routes').then(
            m => m.SETTINGS_ROUTES
          ),
      },
      {
        path: 'VismaExpense',
        component: VismaExpenseComponent,
        canActivate: [canEnterVismaExpense],
      },
      {
        path: FleetRouteName.Main,
        loadChildren: () =>
          import('./fleet/fleet.routes').then(m => m.FLEET_ROUTES),
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.FleetRead]),
        ],
      },
      {
        path: OnCallRouteName.Main,
        component: OnCallPageComponent,
        canActivate: [
          () => userRolesAndPermissionsGuard([RolesAndPermissions.StandbyRead]),
          () => isFeatureEnabledGuard(Feature.Standby),
        ],
      },
      {
        path: RouteNetworkRouteName.Main,
        canActivate: [() => userDomainGuard(2)],
        loadChildren: () =>
          import('./route-network-page/route-network.routes').then(
            m => m.ROUTE_NETWORK_ROUTES
          ),
      },
      {
        path: VippsPaymentsRouteName.Main,
        canActivate: [() => userDomainGuard(2)],
        loadChildren: () =>
          import('./vipps-payments/vipps-payments-routes').then(
            m => m.VIPPS_PAYMENTS_ROUTES
          ),
      },
    ],
    canActivate: [authGuard, applicationMetaDataGuard],
  },
];

@NgModule({
  imports: [MainLayoutComponent, RouterModule.forChild(pagesRoutes)],
  exports: [RouterModule],
})
export class PagesRoutingModule {}
